import { trimDecimals } from '@einhaus/meteoric-helpers';
import type { PortfolioBacktest, PostTradeBalances, TradingStrategyBacktestTrade } from '@einhaus/project314-types';
import { calculateSharpeRatioFromBalanceSnapshots } from '@einhaus/project314-shared';

export function calculateMaxDrawdownFromPostTradeBalances(balances: PostTradeBalances[]): number {
    if (!balances[0]) return 0;
    let peak = balances[0].balance;
    let maxDrawdown = 0;

    for (const data of balances) {
        if (data.balance > peak) peak = data.balance;

        const drawdown = (peak - data.balance) / peak;

        if (drawdown > maxDrawdown) maxDrawdown = drawdown;
    }

    return trimDecimals(-maxDrawdown * 100);
}

export function calculateSharpeRatioFromPostTradeBalances(
    balances: PostTradeBalances[],
    riskFreeRatePerPeriod = 0, // e.g. daily risk-free rate
    periodsPerYear = 252 // annualization convention for the supplied risk-free rate
): number | null {
    return calculateSharpeRatioFromBalanceSnapshots({
        balances,
        riskFreeRatePerPeriod,
        periodsPerYear
    });
}

export function calculateVolatilityFromPostTradeBalances(
    balances: PostTradeBalances[],
    periodsPerYear = 252, // set to 1 for non-annualized
    useSampleStd = true // true → divide by (N–1), false → divide by N
): number {
    const n = balances.length - 1;
    if (n < 1) return 0;

    // 1) compute returns
    const returns = balances.slice(1).map((curr, i) => {
        const prev = balances[i].balance;
        return (curr.balance - prev) / prev;
    });

    // 2) mean return
    const meanReturn = returns.reduce((sum, r) => sum + r, 0) / n;

    // 3) variance (sample or population)
    const divisor = useSampleStd ? n - 1 : n;

    const variance = returns.reduce((sum, r) => sum + Math.pow(r - meanReturn, 2), 0) / divisor;

    // 4) per-period volatility
    const perPeriodVol = Math.sqrt(variance);

    // 5) annualize if desired
    const vol = periodsPerYear > 1 ? perPeriodVol * Math.sqrt(periodsPerYear) : perPeriodVol;

    return trimDecimals(vol * 100);
}

export function calculateSortinoRatioFromPostTradeBalances(
    balances: PostTradeBalances[],
    riskFreeRatePerPeriod = 0, // e.g. daily risk-free rate
    periodsPerYear = 252 // set to 1 for non-annualized
): number {
    if (balances.length < 2) return 0;

    // 1) compute period-to-period returns
    const returns = balances.slice(1).map((curr, i) => {
        const prev = balances[i].balance;
        return (curr.balance - prev) / prev;
    });

    // 2) average return
    const avgReturn = returns.reduce((sum, r) => sum + r, 0) / returns.length;

    // 3) excess return in numerator
    const avgExcessReturn = avgReturn - riskFreeRatePerPeriod;

    // 4) downside returns relative to the target (here riskFreeRatePerPeriod)
    //    i.e. anything below the threshold
    const downside = returns.filter((r) => r < riskFreeRatePerPeriod);

    // if no downside, risk is zero → ratio is infinite (or large number)
    if (downside.length === 0) {
        return Number.POSITIVE_INFINITY;
    }

    // 5) compute downside deviation (population or sample; here sample)
    const varianceDownside = downside.reduce((sum, r) => sum + (r - riskFreeRatePerPeriod) ** 2, 0) / (downside.length - 1);

    const downsideStdDev = Math.sqrt(varianceDownside);

    if (downsideStdDev === 0) {
        return Number.POSITIVE_INFINITY;
    }

    // 6) raw per-period Sortino
    const rawSortino = avgExcessReturn / downsideStdDev;

    // 7) annualize if desired
    const sortino = periodsPerYear > 1 ? rawSortino * Math.sqrt(periodsPerYear) : rawSortino;

    return trimDecimals(sortino);
}

export function calculatePainIndexFromPostTradeBalances(balances: PostTradeBalances[]): number {
    const n = balances.length;
    if (n === 0) return 0;

    let peak = balances[0].balance;
    let totalDrawdown = 0;

    for (const { balance } of balances) {
        // update peak if we're at a new high
        if (balance > peak) peak = balance;

        // drawdown is zero whenever balance >= peak
        const drawdown = (peak - balance) / peak;
        totalDrawdown += drawdown;
    }

    // divide by total periods T (including zeros)
    const avgDrawdown = totalDrawdown / n;

    // returns a fraction (e.g. 0.02 means 2% average drawdown)
    return trimDecimals(avgDrawdown * 100);
}

/** Annualize the saved unitized return: external replenishments are not investment gains.
 * Use the exact valuation interval, including idle time and exits after the entry cutoff.
 * Legacy runs without that evidence remain unavailable rather than using raw account balances.
 */
export function calculatePortfolioCagr(backtest: Pick<PortfolioBacktest, 'status' | 'riskStatistics'>): number | null {
    if (backtest.status !== 'complete') return null;
    const overall = backtest.riskStatistics?.find((stat) => stat.period === 'overall');
    if (
        !overall ||
        !Number.isFinite(overall.returnPercent) ||
        overall.returnPercent < -100 ||
        !Number.isSafeInteger(overall.startTimestamp) ||
        !Number.isSafeInteger(overall.endTimestamp) ||
        overall.startTimestamp <= 0 ||
        overall.endTimestamp <= overall.startTimestamp
    )
        return null;

    const years = (overall.endTimestamp - overall.startTimestamp) / (365.25 * 86400);
    const cagr = Math.expm1(Math.log1p(overall.returnPercent / 100) / years) * 100;
    return Number.isFinite(cagr) ? cagr : null;
}

export function calculateMaxConcurrentTrades(trades: TradingStrategyBacktestTrade[]): number {
    // Step 1: Build an array of trade intervals (start and end times)
    const tradeIntervals: { start: number; end: number; tradeId: number }[] = [];

    // Build intervals for each unique trade
    for (const trade of trades) {
        const timestamps = trade.transactions.map((trade) => trade.timestamp);

        // Collect derivative expiration timestamps if available
        const derivativeExpirations = trade.transactions
            .map((trade) => trade.derivativeExpirationTimestamp)
            .filter((timestamp): timestamp is number => timestamp !== undefined && timestamp !== null);

        const start = Math.min(...timestamps);
        let end = Math.max(...timestamps);

        // Update end time if necessary
        if (end === start && derivativeExpirations.length > 0) {
            const maxDerivativeExpiration = Math.max(...derivativeExpirations);
            if (maxDerivativeExpiration > end) end = maxDerivativeExpiration;
        }

        tradeIntervals.push({ start, end, tradeId: trade.id });
    }

    // Create an array of events where each event is either the start or end of a trade.
    const events: { time: number; delta: number }[] = [];

    tradeIntervals.forEach((trade) => {
        events.push({ time: trade.start, delta: 1 }); // +1 for a trade starting
        events.push({ time: trade.end, delta: -1 }); // -1 for a trade ending
    });

    // Sort the events by time.
    // If two events have the same time, process the end event (delta = -1) before the start event (delta = 1).
    // This prevents counting a position that closes and another that opens at the exact same timestamp as overlapping.
    events.sort((a, b) => {
        if (a.time === b.time) {
            return a.delta - b.delta; // end event comes before start event
        }

        return a.time - b.time;
    });

    let currentOverlap = 0;
    let maxOverlap = 0;

    // Iterate through the events, updating the current overlap count.
    for (const event of events) {
        currentOverlap += event.delta;
        maxOverlap = Math.max(maxOverlap, currentOverlap);
    }

    return maxOverlap;
}
